import type { SiteBuilderBlock } from "@webdo/site-templates/builder";
import {
  siteBuilderPartOptionKey,
} from "@webdo/site-templates/part-options";

export type BuilderInspectorAnimationMode = "" | "appearance" | "scroll";

export function builderInspectorUsesPartScope(
  part: string | null | undefined,
): part is string {
  return Boolean(
    part
    && part !== "root"
    && !part.startsWith("slot:"),
  );
}

export function builderInspectorPartOptionKey(
  part: string | null | undefined,
  key: string,
): string {
  return siteBuilderPartOptionKey(part, key);
}

export function withoutBuilderInspectorStyleConflicts(
  options: Record<string, unknown>,
  fieldKey: string,
  value: unknown,
  part?: string | null,
): Record<string, unknown> {
  if (value === "") {
    return options;
  }

  const nextOptions = {
    ...options,
  };
  const remove = (key: string) => {
    delete nextOptions[builderInspectorPartOptionKey(part, key)];
  };

  if (fieldKey === "backdropBlur") {
    remove("backdropFilter");
  } else if (fieldKey === "backdropFilter") {
    remove("backdropBlur");
  } else if (fieldKey === "padding") {
    for (const key of [
      "paddingTop",
      "paddingRight",
      "paddingBottom",
      "paddingLeft",
    ]) {
      remove(key);
    }
  } else if ([
    "paddingTop",
    "paddingRight",
    "paddingBottom",
    "paddingLeft",
  ].includes(fieldKey)) {
    remove("padding");
  }

  return nextOptions;
}

function optionValue(
  block: SiteBuilderBlock,
  key: string,
  part?: string | null,
): string {
  const value = block.options?.[
    builderInspectorPartOptionKey(part, key)
  ];

  return typeof value === "string" ? value : "";
}

export function builderInspectorAnimationMode(
  block: SiteBuilderBlock,
  part?: string | null,
): BuilderInspectorAnimationMode {
  const value = optionValue(block, "animationMode", part);

  if (value === "appearance" || value === "scroll") {
    return value;
  }

  return optionValue(block, "animation", part)
    ? "appearance"
    : "";
}

export function builderInspectorFieldIsVisible(
  block: SiteBuilderBlock,
  fieldKey: string,
  part?: string | null,
): boolean {
  if (
    block.type === "accordion"
    && (
      fieldKey === "accordionClosedIcon"
      || fieldKey === "accordionOpenIcon"
    )
  ) {
    return block.options?.accordionIconMode === "custom";
  }

  if (
    block.type === "rating"
    && (
      fieldKey === "ratingActiveIcon"
      || fieldKey === "ratingInactiveIcon"
    )
  ) {
    return block.options?.ratingIcon === "custom";
  }

  const mode = builderInspectorAnimationMode(block, part);

  if (
    fieldKey === "animationEffect"
    || fieldKey === "animationDirection"
  ) {
    return mode !== "";
  }

  if (
    fieldKey === "animationTrigger"
    || fieldKey === "animationDuration"
    || fieldKey === "animationStartAfter"
  ) {
    return mode === "appearance";
  }

  if (
    fieldKey === "animationScrollStart"
    || fieldKey === "animationScrollEnd"
  ) {
    return mode === "scroll";
  }

  return true;
}

export function withBuilderInspectorAnimationMode(
  block: SiteBuilderBlock,
  value: string,
  part?: string | null,
): SiteBuilderBlock {
  const keys = {
    direction: builderInspectorPartOptionKey(
      part,
      "animationDirection",
    ),
    duration: builderInspectorPartOptionKey(part, "animationDuration"),
    effect: builderInspectorPartOptionKey(part, "animationEffect"),
    legacy: builderInspectorPartOptionKey(part, "animation"),
    mode: builderInspectorPartOptionKey(part, "animationMode"),
    scrollEnd: builderInspectorPartOptionKey(part, "animationScrollEnd"),
    scrollStart: builderInspectorPartOptionKey(
      part,
      "animationScrollStart",
    ),
    startAfter: builderInspectorPartOptionKey(
      part,
      "animationStartAfter",
    ),
    trigger: builderInspectorPartOptionKey(part, "animationTrigger"),
  };
  const nextOptions = {
    ...block.options,
  };

  delete nextOptions[keys.legacy];

  if (value !== "appearance" && value !== "scroll") {
    for (const key of Object.values(keys)) {
      delete nextOptions[key];
    }
  } else {
    nextOptions[keys.mode] = value;
    nextOptions[keys.effect] = nextOptions[keys.effect] || "fade";
    nextOptions[keys.direction] = nextOptions[keys.direction] || "up";

    if (value === "appearance") {
      nextOptions[keys.trigger] = nextOptions[keys.trigger] || "once";
    } else {
      nextOptions[keys.scrollStart] = nextOptions[keys.scrollStart] || "0%";
      nextOptions[keys.scrollEnd] = nextOptions[keys.scrollEnd] || "60%";
    }
  }

  return {
    ...block,
    options: Object.keys(nextOptions).length > 0
      ? nextOptions
      : undefined,
  };
}
